import _ from "lodash";
import React, { useEffect, useState, useCallback } from "react";
import UIModal from "UIComponents/UIModal";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classNames from "classnames";
import {
  CloseOutlined,
  AddOutlined,
  DotsHorizontalOutlined,
  PencilLineOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";
import {
  IconButton,
  SideNavigation,
  EmptyState,
  Dropdown,
  DropdownMenu,
  Tooltip,
} from "@toddle-edu/ds-web";
import { NoMessagesIllustration } from "@toddle-edu/ds-theme";

import { hideIntercom, showIntercom } from "modules/Services";

import GPTConversation from "./components/GPTConversation";
import CreateConversation from "./components/CreateConversation";
import DeleteConversation from "./components/DeleteConversation";
import classes from "./GPTPlayground.scss";
import { getAiConversationsQuery } from "modules/CommonQuery";
import { getAiConversationsFromCache } from "modules/CommonGraphqlHelpers";

import {
  createAIConversation,
  updateAIConversation,
  deleteAIConversation,
  createNewAIMessage,
} from "./modules/GPTPlaygroundModule";
import { getUserId } from "modules/CommonSelectors";
import { getAIPromptsQuery } from "modules/CommonQuery";
import { getAIPromptsFromCache } from "modules/CommonGraphqlHelpers";
import { useI18n, useImportFromUsageMetrics } from "Hooks";

const styles = {
  childrenAndBannerContainerCustomStyle: {
    overflow: "hidden",
  },
  childrenContainerCustomStyle: {
    overflow: "hidden",
  },
};

const SidebarOptionLabelComponent = ({ label }) => {
  const needsTooltip = _.size(label) > 16;
  return needsTooltip ? (
    <Tooltip placement="bottom" tooltip={label}>
      <div className={classes.optionLabelContainer}>{label}</div>
    </Tooltip>
  ) : (
    <div className={classes.optionLabelContainer}>{label}</div>
  );
};

const getSidebarOptions = ({
  t,
  setIsModalOpen,
  setIsDeleteModalOpen,
  conversations,
  setIsRenameMode,
  setActionConversationDetails,
  handleCreateNewConversation,
}) => {
  const conversationActionOptions = [
    {
      key: "rename",
      label: t("common:rename"),
      icon: <PencilLineOutlined />,
    },
    {
      key: "delete",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
    },
  ];

  const handleConversationAction = ({ id, metadata, key }) => {
    setActionConversationDetails({ id, metadata });
    switch (key) {
      case "rename":
        setIsRenameMode(true);
        setIsModalOpen(true);
        break;
      case "delete":
        setIsDeleteModalOpen(true);
        break;
    }
  };

  const tier2Option = {
    key: "1-1",
    tier: 2,
    title: "New chat",
    suffix: (
      <div className={classes.buttonContainer}>
        <IconButton
          icon={<AddOutlined />}
          theme="dark"
          variant="plain"
          size="small"
          onClick={handleCreateNewConversation}
        />
      </div>
    ),
    parent: "1",
    children: [],
  };

  const tier2OptionChildren = tier2Option.children;
  const tier1Options = [];

  _.forEach(conversations, ({ id, metadata }) => {
    const { label } = metadata || {};
    tier2OptionChildren.push(id);
    tier1Options.push({
      key: id,
      title: <SidebarOptionLabelComponent label={label} />,
      suffix: (
        <div className={classes.optionButtonContainer}>
          <Dropdown
            overlay={
              <DropdownMenu
                options={conversationActionOptions}
                onClick={({ key }) =>
                  handleConversationAction({ id, metadata, key })
                }
              />
            }
            placement={"bottomRight"}
          >
            <IconButton
              size={"x-small"}
              icon={<DotsHorizontalOutlined />}
              // theme="dark"
              variant="outlined"
            />
          </Dropdown>
        </div>
      ),
      tier: 1,
      parent: "1",
      children: [],
    });
  });
  return [
    {
      key: "root",
      children: ["1"],
    },
    {
      key: "1",
      tier: 3,
      parent: "root",
      children: ["1-1"],
    },
    tier2Option,
    ...tier1Options,
  ];
};

const GPTPlayground = props => {
  const {
    createAIConversation,
    updateAIConversation,
    deleteAIConversation,
    createNewAIMessage,
    userId,
    conversations,
    onModalClose,
    hideIntercom,
    showIntercom,
    isPublicView,
    paramsConversationId,
    agentPrompts,
  } = props;

  const defaultConversationId = isPublicView
    ? paramsConversationId
    : _.first(conversations)?.id;
  const [selectedConversationId, setSelectedConversationId] = useState(
    defaultConversationId
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRenameMode, setIsRenameMode] = useState(false);
  const [actionConversationDetails, setActionConversationDetails] = useState(
    undefined
  );

  const handleImportConversationCreated = useCallback(conversationId => {
    setSelectedConversationId(conversationId);
  }, []);

  const { isImporting } = useImportFromUsageMetrics({
    userId,
    createAIConversation,
    onConversationCreated: handleImportConversationCreated,
  });
  const [publicViewTitle, setPublicViewTitle] = useState("");
  const [editMessageId, setEditMessageId] = useState("");

  const { t } = useI18n(["common"]);

  const onCreateConversationModalClose = () => {
    setIsModalOpen(false);
  };

  const onDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    hideIntercom();
    return () => {
      showIntercom();
    };
  }, []);

  const onCreateConversation = async ({ label }) => {
    try {
      setIsLoading(true);
      const response = await createAIConversation({
        input: {
          entityId: userId,
          entityType: "USER",
          metadata: {
            label,
            model: "openai:gpt-4o",
            tools: ["internet_browsing"],
            systemPrompt: "",
          },
        },
      });
      const conversationId = _.get(
        response,
        "data.ai.createAiConversation.response.id"
      );

      // we cannot use optimistic mutation because conversation details in apollo cache is not guarenteed
      const result = await createNewAIMessage({
        aiConversationId: conversationId,
        authorType: "user",
        query: "",
      });

      const messageId = _.get(result, "data.ai.createAiMessage.response.id");

      setEditMessageId(messageId);

      setSelectedConversationId(conversationId);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateNewConversation = async () => {
    await onCreateConversation({ label: "New chat" });
  };

  const handleChangeConversation = id => {
    if (id) {
      setEditMessageId("");
      setSelectedConversationId(id);
    }
  };

  const onRenameConversation = async ({ label }) => {
    setIsRenameMode(false);
    try {
      setIsLoading(true);
      await updateAIConversation({
        input: {
          id: _.get(actionConversationDetails, "id"),
          entityId: userId,
          entityType: "USER",
          metadata: {
            ..._.get(actionConversationDetails, "metadata", {}),
            label,
          },
        },
      });
      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const onDeleteConversation = async () => {
    setIsRenameMode(false);
    try {
      setIsLoading(true);
      await deleteAIConversation({
        input: {
          id: _.get(actionConversationDetails, "id"),
        },
      });

      // set current conversation to next conversation if the current conversation is deleted
      if (
        _.isEqual(
          selectedConversationId,
          _.get(actionConversationDetails, "id")
        )
      ) {
        const newConversation = _.find(
          conversations,
          conversation =>
            !_.isEqual(
              _.get(conversation, "id"),
              _.get(actionConversationDetails, "id")
            )
        );
        setEditMessageId("");
        setSelectedConversationId(_.get(newConversation, "id", null));
      }

      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const sidebarOptions = getSidebarOptions({
    t,
    setIsModalOpen,
    setIsDeleteModalOpen,
    conversations,
    setIsRenameMode,
    setActionConversationDetails,
    handleCreateNewConversation,
  });

  const conversationViewClasses = classNames({
    [classes.conversationViewContainer]: !isPublicView,
    [classes.conversationViewContainerPublic]: isPublicView,
  });

  return (
    <>
      <UIModal
        isOpen={true}
        isFullScreen={true}
        childrenContainerCustomStyle={styles.childrenContainerCustomStyle}
        childrenAndBannerContainerCustomStyle={
          styles.childrenAndBannerContainerCustomStyle
        }
      >
        <div className={classes.container}>
          <div className={classes.headerContainer}>
            <div>{!isPublicView ? "Playground" : publicViewTitle}</div>
            {!isPublicView ? (
              <IconButton
                variant="plain"
                icon={<CloseOutlined />}
                onClick={onModalClose}
              />
            ) : null}
          </div>
          <div className={classes.bodyContainer}>
            {!isPublicView ? (
              <div className={classes.sidebarContainer}>
                <SideNavigation
                  options={sidebarOptions}
                  selectedOptionKey={selectedConversationId}
                  onClick={handleChangeConversation}
                />
              </div>
            ) : null}
            {!_.isEmpty(conversations) || isPublicView ? (
              <div className={conversationViewClasses}>
                <GPTConversation
                  key={"AI_CONVERSATION_" + selectedConversationId}
                  aiConversationId={selectedConversationId}
                  isPublicView={isPublicView}
                  setPublicViewTitle={setPublicViewTitle}
                  setIsLoading={setIsLoading}
                  editMessageId={editMessageId}
                  setEditMessageId={setEditMessageId}
                  agentPrompts={agentPrompts}
                />
              </div>
            ) : (
              <div className={classes.emptyStateContainer}>
                <EmptyState
                  illustration={NoMessagesIllustration}
                  title={t("common:no_thread_selected")}
                  subtitle={t("common:no_thread_selected_subtext")}
                />
              </div>
            )}
          </div>
        </div>
      </UIModal>
      {isModalOpen && (
        <CreateConversation
          isRenameMode={isRenameMode}
          onCreateConversation={onCreateConversation}
          onRenameConversation={onRenameConversation}
          onModalClose={onCreateConversationModalClose}
          actionConversationDetails={actionConversationDetails}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConversation
          onDeleteConversation={onDeleteConversation}
          onModalClose={onDeleteModalClose}
        />
      )}
      {(isLoading || isImporting) && (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      )}
    </>
  );
};

const mapActionCreators = {
  createAIConversation,
  updateAIConversation,
  deleteAIConversation,
  createNewAIMessage,
  hideIntercom,
  showIntercom,
};

const mapStateToProps = (state, ownProps) => {
  const conversationId = _.get(ownProps, "params.conversationId", "");

  const isPublicView = !_.isEmpty(conversationId);

  return {
    isData: true,
    isLoading: false,
    userId: getUserId(state),
    isPublicView,
    paramsConversationId: conversationId,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAiConversationsQuery, {
    name: "getAiConversations",
    skip: ({ isPublicView }) => isPublicView,
    options: ({ userId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        filters: {
          entityId: userId,
          entityType: "USER",
        },
      },
    }),
    props({
      getAiConversations: { networkStatus, variables },
      ownProps: { isData, isLoading },
    }) {
      const data = getAiConversationsFromCache(variables);

      const conversationEdges = _.get(data, "aiConversations.edges", []);
      return {
        conversations: _.map(conversationEdges, ({ node }) => ({
          ...node,
        })),
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAIPromptsQuery, {
    name: "getAIAgentPrompts",
    options: () => ({
      fetchPolicy: "cache-and-network",
      variables: {
        filters: {
          entityType: "AI_PLAYGROUND",
        },
      },
    }),
    props({ getAIAgentPrompts, ownProps: { isData, isLoading } }) {
      const queryData = getAIPromptsFromCache(getAIAgentPrompts.variables);
      const agentPrompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getAIAgentPrompts, "networkStatus");
      return {
        agentPrompts,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(GPTPlayground);
